import React from "react";
import { useDelete } from "../../../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../../../../i18n";
import ModalForm from "@ui/Modal/Form";
import { useSetups, type Setup } from "../../../../../../api/useSetups";
import type { Project } from "../../../../../../api/useProjects";
import Text from "@ui/Text";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { useImpactedAnalyses } from "../../useAnalysisImpacted";
import useActiveAnalysesStream from "../../../../../../api/useActiveAnalysesStream";

interface P {
  onRequestClose: () => void;
  project: Project;
  setupName: string;
  setup: Setup;
}

const analysisStatus = new Set(["DIAGNOSIS", "DIAGNOSIS_ERROR"] as const);

export const DeleteObservables = ({ project, setupName, setup, onRequestClose }: P) => {
  const { addToast } = useToasts();
  const setupId = setup.id;
  const projectId = project.id;
  const deleteObservables = useDelete(
    "/projects/:projectId(uuid)/setups/:setupId(uuid)/diagnosis/inputs",
  );
  const { activeAnalyses } = useActiveAnalysesStream(projectId);

  const impactedAnalyses = useImpactedAnalyses({
    setups: useSetups(projectId),
    activeAnalyses,
    setupId,
    analysisStatus,
  });
  const showAdditionalMessage = setup.diagnosisStatus === "COMPLETE" && impactedAnalyses.length > 0;

  const handleSubmit = React.useCallback(async () => {
    await deleteObservables({ routeParams: { projectId, setupId } });
    addToast(
      "success",
      t("setup.diagnosis.observable.delete_observables.toast.success", { name: setupName }),
    );
    return true;
  }, [addToast, deleteObservables, projectId, setupId, setupName]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      onSubmit={handleSubmit}
      okButtonName={t("modal.delete")}
      okButtonVariant="destructive"
    >
      <Text>{t("setup.diagnosis.observable.delete_observables.body")}</Text>
      {showAdditionalMessage ? (
        <AdditionnalMessage
          status="WARNING"
          text={t("setup.modal.delete.analysis.additionnal_message.warning", {
            diagnosisValid: setup.diagnosisStatus === "COMPLETE",
            impactedAnalysisCount: impactedAnalyses.length,
          })}
        />
      ) : null}
    </ModalForm>
  );
};
